import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Plus, X, DownloadSimple } from '@phosphor-icons/react';
import ListSearch, { filterBySearch } from './ListSearch';
import { ModuleShell, SkeletonBlock } from './module-ui';
import SlaPipelinePanel from './SlaPipelinePanel';
import { apiPath } from '../config/apiBase';

const API = apiPath('/api');
const TICKETS_API = apiPath('/api/tickets');

const TABS = [
  { id: 'pipeline', label: 'SLA Pipeline' },
  { id: 'policies', label: 'Policies' },
];

function SLAPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some((t) => t.id === searchParams.get('tab'))
    ? searchParams.get('tab')
    : 'pipeline';
  const pipelineState = searchParams.get('state') || 'all';

  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    priority: 'medium',
    response_time_hours: 8,
    resolution_time_hours: 24,
  });

  useEffect(() => {
    if (tab === 'policies') fetchPolicies();
  }, [tab]);

  const fetchPolicies = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/sla-policies`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` },
      });
      setPolicies(response.data);
    } catch (error) {
      console.error('Failed to fetch SLA policies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePolicy = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/sla-policies`, formData, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowCreateModal(false);
      setFormData({ name: '', priority: 'medium', response_time_hours: 8, resolution_time_hours: 24 });
      fetchPolicies();
    } catch (error) {
      console.error('Failed to create SLA policy:', error);
      alert('Failed to create SLA policy');
    }
  };

  const setTab = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', id);
    if (id !== 'pipeline') next.delete('state');
    setSearchParams(next, { replace: true });
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${TICKETS_API}/dashboard/sla-pipeline/export.csv`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` },
        params: { state: pipelineState === 'all' ? 'all' : pipelineState },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sla-pipeline-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export SLA data:', error);
      alert('Failed to export SLA data');
    }
  };

  const filteredPolicies = useMemo(
    () => filterBySearch(policies, searchQuery, ['sla_id', 'name', 'priority']),
    [policies, searchQuery],
  );

  return (
    <ModuleShell
      title="SLA Management"
      subtitle="Breached & at-risk pipeline, policies, and full SLA data export"
      headerActions={(
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {tab === 'pipeline' && (
            <button
              type="button"
              onClick={handleExport}
              className="btn-secondary"
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              data-testid="sla-page-export-button"
            >
              <DownloadSimple size={18} weight="bold" />
              Export SLA data
            </button>
          )}
          {tab === 'policies' && (
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              data-testid="create-sla-button"
            >
              <Plus size={18} weight="bold" />
              Create SLA Policy
            </button>
          )}
        </div>
      )}
    >
      <div data-testid="sla-page">
        <div className="sla-module-tabs" role="tablist" aria-label="SLA sections">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={tab === t.id ? 'is-active' : ''}
              onClick={() => setTab(t.id)}
              data-testid={`sla-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'pipeline' && (
          <SlaPipelinePanel
            initialState={pipelineState}
            showExport={false}
            embedTitle
          />
        )}

        {tab === 'policies' && (
          <>
            <ListSearch
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search SLA policies…"
              resultCount={filteredPolicies.length}
              totalCount={policies.length}
            />

            {loading ? (
              <SkeletonBlock />
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>SLA ID</th>
                      <th>Name</th>
                      <th>Priority</th>
                      <th>Response Time (Hours)</th>
                      <th>Resolution Time (Hours)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPolicies.map((policy) => (
                      <tr key={policy.sla_id} data-testid="sla-row">
                        <td style={{ fontFamily: 'IBM Plex Mono, monospace', fontWeight: '600' }}>{policy.sla_id}</td>
                        <td>{policy.name}</td>
                        <td>
                          <span className={`badge badge-${policy.priority}`}>{policy.priority}</span>
                        </td>
                        <td>{policy.response_time_hours}h</td>
                        <td>{policy.resolution_time_hours}h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {showCreateModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
            data-testid="create-sla-modal"
          >
            <div style={{ background: 'white', padding: '32px', width: '100%', maxWidth: '600px', borderRadius: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h2 style={{ fontSize: '15px', fontWeight: '600' }}>Create SLA Policy</h2>
                <button type="button" onClick={() => setShowCreateModal(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleCreatePolicy}>
                <div style={{ marginBottom: '8px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Policy Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input-field"
                    required
                    data-testid="sla-name-input"
                  />
                </div>

                <div style={{ marginBottom: '8px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="input-field"
                    data-testid="sla-priority-select"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>

                <div style={{ marginBottom: '8px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Response Time (Hours)</label>
                  <input
                    type="number"
                    value={formData.response_time_hours}
                    onChange={(e) => setFormData({ ...formData, response_time_hours: parseInt(e.target.value, 10) })}
                    className="input-field"
                    required
                    min="1"
                    data-testid="sla-response-input"
                  />
                </div>

                <div style={{ marginBottom: '10px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Resolution Time (Hours)</label>
                  <input
                    type="number"
                    value={formData.resolution_time_hours}
                    onChange={(e) => setFormData({ ...formData, resolution_time_hours: parseInt(e.target.value, 10) })}
                    className="input-field"
                    required
                    min="1"
                    data-testid="sla-resolution-input"
                  />
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="submit" className="btn-primary" style={{ flex: 1 }} data-testid="submit-sla-button">
                    Create Policy
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="btn-secondary"
                    style={{ flex: 1 }}
                    data-testid="cancel-sla-button"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </ModuleShell>
  );
}

export default SLAPage;
